import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Footer } from "./Footer";

const SECTIONS = [
  {
    n: "01",
    title: "Information We Collect",
    blocks: [
      { type: "lead", text: "We may collect the following information:" },
      { type: "subhead", text: "Personal Information" },
      { type: "ul", items: ["Full name", "Email address", "Mobile phone number", "Date of birth (where required)", "Profile photo", "Home city or province"] },
      { type: "subhead", text: "Driver Information" },
      { type: "p", text: "Drivers may also be required to provide:" },
      { type: "ul", items: ["Valid driver's licence information", "Vehicle make, model, year, colour, and licence plate number", "Vehicle registration", "Proof of insurance", "Driver verification documents", "Banking or payout information"] },
      { type: "subhead", text: "Trip Information" },
      { type: "p", text: "We collect information about trips, including:" },
      { type: "ul", items: ["Pickup and destination locations", "Route information", "Date and time of travel", "Passenger and driver details", "Booking history", "Trip status", "Ratings and reviews"] },
      { type: "subhead", text: "Location Information" },
      { type: "p", text: "With your permission, Saferide may collect precise or approximate location information while you are using the Services to:" },
      { type: "ul", items: ["Match riders with nearby drivers", "Provide navigation assistance", "Improve pickup accuracy", "Enhance user safety", "Respond to emergencies"] },
      { type: "p", text: "You may disable location services through your device settings, although some features may not function properly." },
      { type: "subhead", text: "Payment Information" },
      { type: "p", text: "Payments are securely processed through third-party payment providers. Saferide does not store complete credit or debit card numbers on its servers." },
      { type: "subhead", text: "Device Information" },
      { type: "p", text: "We may automatically collect:" },
      { type: "ul", items: ["IP address", "Device type", "Operating system", "App version", "Browser information", "Device identifiers", "Crash reports", "Usage analytics"] },
    ],
  },
  {
    n: "02",
    title: "How We Use Your Information",
    blocks: [
      { type: "lead", text: "We use your information to:" },
      { type: "ul", items: ["Create and manage user accounts", "Facilitate ride bookings", "Connect riders and drivers", "Process payments and payouts", "Verify identities", "Improve customer service", "Prevent fraud and unauthorized activity", "Maintain platform security", "Send booking confirmations and service notifications", "Respond to customer support requests", "Comply with legal obligations", "Improve our products and services"] },
    ],
  },
  {
    n: "03",
    title: "How We Share Information",
    blocks: [
      { type: "lead", text: "Saferide may share information with:" },
      { type: "subhead", text: "Riders and Drivers" },
      { type: "p", text: "Limited information necessary to complete a trip, including:" },
      { type: "ul", items: ["First name", "Profile photo", "Vehicle information (for drivers)", "Pickup and destination details"] },
      { type: "subhead", text: "Service Providers" },
      { type: "p", text: "Third-party providers assisting with:" },
      { type: "ul", items: ["Payment processing", "Cloud hosting", "Customer support", "Identity verification", "Analytics", "Communications"] },
      { type: "subhead", text: "Legal Requirements" },
      { type: "p", text: "We may disclose information when required by law or when reasonably necessary to:" },
      { type: "ul", items: ["Comply with legal obligations", "Protect the safety of users", "Investigate fraud or criminal activity", "Respond to lawful requests from government authorities"] },
      { type: "p", text: "Saferide does not sell or rent users' personal information to third parties." },
    ],
  },
  {
    n: "04",
    title: "Data Security",
    blocks: [
      { type: "p", text: "Saferide uses commercially reasonable administrative, technical, and physical safeguards to protect personal information from unauthorized access, alteration, disclosure, or destruction." },
      { type: "p", text: "Although we strive to protect your information, no method of electronic storage or internet transmission is completely secure." },
    ],
  },
  {
    n: "05",
    title: "Data Retention",
    blocks: [
      { type: "lead", text: "We retain personal information only for as long as necessary to:" },
      { type: "ul", items: ["Provide our Services", "Meet legal and regulatory requirements", "Resolve disputes", "Enforce our agreements", "Prevent fraud"] },
      { type: "p", text: "When information is no longer required, it will be securely deleted or anonymized where appropriate." },
    ],
  },
  {
    n: "06",
    title: "Your Privacy Rights",
    blocks: [
      { type: "lead", text: "Subject to applicable law, you may have the right to:" },
      { type: "ul", items: ["Access your personal information", "Correct inaccurate information", "Update your account details", "Request deletion of your account", "Withdraw consent where applicable", "Request a copy of your personal information"] },
      { type: "p", text: "Some requests may be limited where Saferide is legally required to retain certain records." },
    ],
  },
  {
    n: "07",
    title: "Cookies and Similar Technologies",
    blocks: [
      { type: "lead", text: "Saferide uses cookies and similar technologies to:" },
      { type: "ul", items: ["Remember user preferences", "Maintain secure login sessions", "Improve application performance", "Measure usage and analytics", "Detect fraud"] },
      { type: "p", text: "You may manage cookie settings through your browser or device." },
    ],
  },
  {
    n: "08",
    title: "Children's Privacy",
    blocks: [
      { type: "p", text: "Saferide is not intended for individuals under 18 years of age. We do not knowingly collect personal information from children." },
      { type: "p", text: "If we become aware that information has been collected from a child without appropriate authorization, we will promptly delete it." },
    ],
  },
  {
    n: "09",
    title: "International Data Transfers",
    blocks: [
      { type: "p", text: "Your information may be processed or stored in Canada or other countries where our service providers operate. By using Saferide, you acknowledge that your information may be transferred across jurisdictions with different privacy laws." },
    ],
  },
  {
    n: "10",
    title: "Changes to This Privacy Policy",
    blocks: [
      { type: "p", text: "Saferide may update this Privacy Policy from time to time. Material changes will be communicated through the app, website, or by email where appropriate." },
      { type: "p", text: "Your continued use of the Services after changes take effect constitutes acceptance of the updated Privacy Policy." },
    ],
  },
  {
    n: "11",
    title: "Contact Us",
    blocks: [
      { type: "p", text: "If you have questions or concerns about this Privacy Policy or how your information is handled, please contact us:" },
      { type: "contact" },
    ],
  },
  {
    n: "12",
    title: "Compliance with Canadian Privacy Laws",
    blocks: [
      { type: "p", text: "Saferide is committed to complying with the Personal Information Protection and Electronic Documents Act (PIPEDA) and other applicable Canadian privacy laws. Where provincial privacy legislation applies, Saferide will comply with those laws as required." },
      { type: "p", text: "Users may contact us to request access to, correction of, or deletion of their personal information, subject to applicable legal requirements." },
    ],
  },
];

const Block = ({ block }) => {
  if (block.type === "p")
    return <p className="mb-4 text-base leading-relaxed text-[#565b73]">{block.text}</p>;
  if (block.type === "lead")
    return <p className="mb-4 mt-6 font-display text-base font-semibold text-[#12142b] first:mt-0">{block.text}</p>;
  if (block.type === "subhead")
    return (
      <p className="mb-3 mt-8 flex items-center gap-3 font-display text-xs font-semibold uppercase tracking-[0.2em] text-[#2340b5]">
        <span className="h-px w-6 bg-[#2340b5]" />
        {block.text}
      </p>
    );
  if (block.type === "ul")
    return (
      <ul className="mb-4 space-y-2.5">
        {block.items.map((it) => (
          <li key={it} className="flex gap-3 text-base leading-relaxed text-[#565b73]">
            <span className="mt-2.5 h-1.5 w-1.5 shrink-0 bg-[#2340b5]" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    );
  if (block.type === "contact")
    return (
      <div className="mt-2 border border-[#12142b]/10 bg-[#f4f6fb] p-6">
        <p className="font-display text-lg font-bold text-[#12142b]">Saferide Privacy Officer</p>
        <p className="mt-3 text-base text-[#565b73]">
          Email:{" "}
          <a href="mailto:saferidecarppool@gmail.com" className="text-[#2340b5] underline-offset-4 hover:underline">
            saferidecarppool@gmail.com
          </a>
        </p>
      </div>
    );
  return null;
};

export default function PrivacyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-50 border-b border-[#12142b]/10 bg-white/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1000px] items-center justify-between px-6 py-4 lg:px-8">
          <Link to="/" data-testid="privacy-logo" className="flex items-center gap-2.5">
            <img src="/saferide-mark.png" alt="Saferide" className="h-8 w-auto" />
            <span className="font-display text-lg font-extrabold tracking-tight text-[#12142b]">
              Safe<span className="text-[#2340b5]">ride</span>
            </span>
          </Link>
          <Link
            to="/"
            data-testid="privacy-back-home"
            className="group inline-flex items-center gap-2 font-display text-sm font-medium text-[#565b73] transition-colors hover:text-[#2340b5]"
          >
            <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" strokeWidth={1.5} />
            Back home
          </Link>
        </div>
      </header>

      <div className="mx-auto max-w-[1000px] px-6 pb-12 pt-20 lg:px-8 lg:pt-28">
        <p className="mb-6 flex items-center gap-3 font-display text-xs uppercase tracking-[0.28em] text-[#2340b5]">
          <span className="h-px w-10 bg-[#2340b5]" /> Legal · Effective July 9, 2026
        </p>
        <h1 className="font-display text-4xl font-extrabold leading-none tracking-tighter text-[#12142b] sm:text-5xl lg:text-6xl">
          Privacy Policy
        </h1>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#565b73]">
          At Saferide, your privacy is important to us. This Privacy Policy explains how we collect, use,
          disclose, store, and protect your personal information when you use the Saferide mobile application,
          website, and related services (collectively, the "Services").
        </p>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-[#565b73]">
          By creating an account or using Saferide, you consent to the collection and use of your information
          as described in this Privacy Policy.
        </p>
      </div>

      <main className="mx-auto max-w-[1000px] px-6 pb-24 lg:px-8">
        <div className="border-t border-[#12142b]/10">
          {SECTIONS.map((s) => (
            <section
              key={s.n}
              data-testid={`privacy-section-${s.n}`}
              className="grid grid-cols-1 gap-6 border-b border-[#12142b]/10 py-12 md:grid-cols-12 md:gap-10"
            >
              <div className="md:col-span-4">
                <div className="flex items-baseline gap-4 md:sticky md:top-24">
                  <span className="font-display text-3xl font-black leading-none text-[#12142b]/10">{s.n}</span>
                  <h2 className="font-display text-xl font-bold tracking-tight text-[#12142b] lg:text-2xl">
                    {s.title}
                  </h2>
                </div>
              </div>
              <div className="md:col-span-8">
                {s.blocks.map((b, i) => (
                  <Block key={i} block={b} />
                ))}
              </div>
            </section>
          ))}
        </div>
      </main>

      <Footer />
    </div>
  );
}
